import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "expo-router";
import { useForm } from "react-hook-form";
import { JobCrewFeedbackFormValues, defaultJobCrewFeedbackValues, jobCrewFeedbackSchema, mapFeedbackForBackend, ratingFields } from "./types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Form, FormField, FormLabel, Label, Textarea, Switch, RadioGroup, RadioGroupItemWithLabel, Button, FormInput, FormMessage } from "../ui";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue, type Option } from "../ui/select";
import { View } from "react-native";
import { useState } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { StarRating } from "@/components/ui/star-rating";
import { trpc } from "@/trpc/react";
import Toast from "react-native-toast-message";
import { P } from "../ui/typography";
import { useCrew } from "@/context/CrewProvider";
import { Text } from "../ui/text";


interface FeedbackFormProps {
  jobCrewId: number;
  crewMemberName?: string;
}

const ARRIVAL_STATUS_LABELS: Record<string, string> = {
  arrived_10_mins_early_prepared: "Arrived 10 minutes early, in full uniform, and fully prepared",
  arrived_within_10_mins_prepared: "Arrived within 10 minutes before start time, in full uniform, ready",
  arrived_before_call_not_prepared: "Arrived before call time but not prepared or not in full uniform",
  late_arrival: "Late arrival"
};

export default function FeedbackForm({ jobCrewId, crewMemberName }: FeedbackFormProps) {
  const [lateValue, setLateValue] = useState('');

  const router = useRouter();
  const insets = useSafeAreaInsets();
  const contentInsets = {
    top: insets.top,
    bottom: insets.bottom,
    left: 12,
    right: 12,
  };

  const form = useForm<JobCrewFeedbackFormValues>({
    resolver: zodResolver(jobCrewFeedbackSchema),
    defaultValues: defaultJobCrewFeedbackValues,
  });

  const onLabelPress = (value: string) => () => setLateValue(value);


  const { mutate: createFeedback, isPending } = trpc.feedback.create.useMutation({
    onSuccess(_response) {
      router.back();
      Toast.show({
        type: 'success',
        text1: 'Feedback successfully submitted!'
      });
    },
  });

  const { currentCrewMember } = useCrew();
  const authorID = currentCrewMember?.id || 0;

  async function onSubmit(data: JobCrewFeedbackFormValues) {
    const mappedValues = mapFeedbackForBackend({ ...data, job_crew_id: jobCrewId }, lateValue as keyof JobCrewFeedbackFormValues, authorID);
    createFeedback(mappedValues);
  };

  return (
    <Form {...form}>
      <Card className='mt-4 mb-4'>
        <CardHeader>
          <CardTitle>Strikes</CardTitle>
          {/* These toggles ARE the strike record. Chiefs were answering them believing it was
              "just feedback" — saying so here is the difference between a considered answer
              and a tap. */}
          <CardDescription>
            Answering No to any of these records a strike against the crew member. Late to call is
            worth 2 and a no show 3; everything else is 1. Three in a calendar month goes to a
            manager to decide on.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <View className='flex flex-row items-center gap-4 mb-5'>
            <FormLabel className='w-40'>Appearance acceptable?</FormLabel>
            <FormField
              control={form.control}
              name="appearance"
              render={({ field }) => (
                <View className='flex-row items-center gap-2'>
                  <Label
                    nativeID='appearance'
                    onPress={() => {
                      field.onChange((prev: boolean) => !prev);
                    }}
                  >
                    No
                  </Label>
                  <Switch checked={field.value} onCheckedChange={field.onChange} nativeID='appearance' />
                  <Label
                    nativeID='appearance'
                    onPress={() => {
                      field.onChange((prev: boolean) => !prev);
                    }}
                  >
                    Yes
                  </Label>
                </View>
              )}
            />
          </View>
          {!form.watch('appearance') && (
            <View className='flex flex-row items-center gap-4 mb-5'>
              <FormField
                control={form.control}
                name="appearance_comment"
                render={({ field }) => (
                  <View className='flex-1'>
                    <FormLabel>Appearance Comments</FormLabel>
                    <Textarea
                      {...field}
                      onChangeText={(value) => {
                        field.onChange(value);
                      }}
                      value={field.value || ''}
                    />
                  </View>
                )}
              />
            </View>
          )}
          <View className='flex flex-row items-center gap-4 mb-5'>
            <FormLabel className='w-40'>PPE Complete?</FormLabel>
            <FormField
              control={form.control}
              name="ppe"
              render={({ field }) => (
                <View className='flex-row items-center gap-2'>
                  <Label
                    nativeID='ppe'
                    onPress={() => {
                      field.onChange((prev: boolean) => !prev);
                    }}
                  >
                    No
                  </Label>
                  <Switch checked={field.value} onCheckedChange={field.onChange} nativeID='ppe' />
                  <Label
                    nativeID='ppe'
                    onPress={() => {
                      field.onChange((prev: boolean) => !prev);
                    }}
                  >
                    Yes
                  </Label>
                </View>
              )}
            />
          </View>
          {!form.watch('ppe') && (
            <View className='flex flex-row items-center gap-4 mb-5'>
              <FormField
                control={form.control}
                name="ppe_comment"
                render={({ field }) => (
                  <View className='flex-1'>
                    <FormLabel>PPE Comments</FormLabel>
                    <Textarea
                      {...field}
                      onChangeText={(value) => {
                        field.onChange(value);
                      }}
                      value={field.value || ''}
                    />
                  </View>
                )}
              />
            </View>
          )}
          <View className='flex flex-row items-center gap-4 mb-5'>
            <FormLabel className='w-40'>Photo ID present?</FormLabel>
            <FormField
              control={form.control}
              name="photo_id"
              render={({ field }) => (
                <View className='flex-row items-center gap-2'>
                  <Label
                    nativeID='photo_id'
                    onPress={() => {
                      field.onChange((prev: boolean) => !prev);
                    }}
                  >
                    No
                  </Label>
                  <Switch checked={field.value} onCheckedChange={field.onChange} nativeID='photo_id' />
                  <Label
                    nativeID='photo_id'
                    onPress={() => {
                      field.onChange((prev: boolean) => !prev);
                    }}
                  >
                    Yes
                  </Label>
                </View>
              )}
            />
          </View>
          <View className='flex flex-row items-center gap-4 mb-5'>
            <FormLabel className='w-40'>No unauthorised smoke breaks?</FormLabel>
            <FormField
              control={form.control}
              name="no_unauthorised_smoke_break"
              render={({ field }) => (
                <View className='flex-row items-center gap-2'>
                  <Label
                    nativeID='no_unauthorised_smoke_break'
                    onPress={() => {
                      field.onChange((prev: boolean) => !prev);
                    }}
                  >
                    No
                  </Label>
                  <Switch checked={field.value} onCheckedChange={field.onChange} nativeID='no_unauthorised_smoke_break' />
                  <Label
                    nativeID='no_unauthorised_smoke_break'
                    onPress={() => {
                      field.onChange((prev: boolean) => !prev);
                    }}
                  >
                    Yes
                  </Label>
                </View>
              )}
            />
          </View>
          <View className='flex flex-row items-center gap-4 mb-5'>
            <FormLabel className='w-40'>No excessive phone use?</FormLabel>
            <FormField
              control={form.control}
              name="no_excessive_phone_use"
              render={({ field }) => (
                <View className='flex-row items-center gap-2'>
                  <Label
                    nativeID='no_excessive_phone_use'
                    onPress={() => {
                      field.onChange((prev: boolean) => !prev);
                    }}
                  >
                    No
                  </Label>
                  <Switch checked={field.value} onCheckedChange={field.onChange} nativeID='no_excessive_phone_use' />
                  <Label
                    nativeID='no_excessive_phone_use'
                    onPress={() => {
                      field.onChange((prev: boolean) => !prev);
                    }}
                  >
                    Yes
                  </Label>
                </View>
              )}
            />
          </View>
          <View className='flex flex-row items-center gap-4 mb-5'>
            <FormLabel className='w-40'>Attitude acceptable?</FormLabel>
            <FormField
              control={form.control}
              name="attitude"
              render={({ field }) => (
                <View className='flex-row items-center gap-2'>
                  <Label
                    nativeID='attitude'
                    onPress={() => {
                      field.onChange((prev: boolean) => !prev);
                    }}
                  >
                    No
                  </Label>
                  <Switch checked={field.value} onCheckedChange={field.onChange} nativeID='attitude' />
                  <Label
                    nativeID='attitude'
                    onPress={() => {
                      field.onChange((prev: boolean) => !prev);
                    }}
                  >
                    Yes
                  </Label>
                </View>
              )}
            />
          </View>
          {!form.watch('attitude') && (
            <View className='flex flex-row items-center gap-4 mb-5'>
              <FormField
                control={form.control}
                name="attitude_comment"
                render={({ field }) => (
                  <View className='flex-1'>
                    <FormLabel>Attitude Comments</FormLabel>
                    <Textarea
                      {...field}
                      onChangeText={(value) => {
                        field.onChange(value);
                      }}
                      value={field.value || ''}
                    />
                  </View>
                )}
              />
            </View>
          )}
          <View className='flex flex-row items-center gap-4 mb-5'>
            <FormLabel className='w-40'>On Time?</FormLabel>
            <FormField
              control={form.control}
              name="on_time"
              render={({ field }) => (
                <View className='flex-row items-center gap-2'>
                  <Label
                    nativeID='on_time'
                    onPress={() => {
                      field.onChange((prev: boolean) => !prev);
                    }}
                  >
                    No
                  </Label>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      // Saying they were on time hides the "How late?" list. Clear what it
                      // held, or the old answer is submitted invisibly: pick No show, flip
                      // this back to yes, and mapFeedbackForBackend still writes no_show from
                      // the stale lateValue. That issues a no-show strike for a shift marked
                      // on time. Matches the same fix in the office feedback form.
                      if (checked) setLateValue('');
                    }}
                    nativeID='on_time'
                  />
                  <Label
                    nativeID='on_time'
                    onPress={() => {
                      field.onChange((prev: boolean) => !prev);
                    }}
                  >
                    Yes
                  </Label>
                </View>
              )}
            />
          </View>
          {!form.watch('on_time') && (
            <>
              <FormLabel>How late?</FormLabel>
              <View className='pt-3 mb-5'>
                <RadioGroup value={lateValue} onValueChange={setLateValue} className='gap-3'>
                  <RadioGroupItemWithLabel value='late_0_to_15' onLabelPress={onLabelPress('late_0_to_15')} label='0-15 mins' />
                  <RadioGroupItemWithLabel value='late_15_to_30' onLabelPress={onLabelPress('late_15_to_30')} label='15-30 mins' />
                  <RadioGroupItemWithLabel value='late_30_to_45' onLabelPress={onLabelPress('late_30_to_45')} label='30-40 mins' />
                  <RadioGroupItemWithLabel value='late_1hr' onLabelPress={onLabelPress('late_1hr')} label="1h plus" />
                  <RadioGroupItemWithLabel value='no_show' onLabelPress={onLabelPress('no_show')} label='No show' />
                </RadioGroup>
              </View>

              {lateValue === 'late_1hr' && (
                <View className='flex flex-row items-center gap-4 mb-5'>
                  <View className='flex-1'>
                    <FormLabel>What time did they arrive?</FormLabel>
                    <FormField
                      control={form.control}
                      name="late_time"
                      render={({ field }) => (
                        <FormInput
                          {...field}
                          value={field.value}
                        />
                      )}
                    />
                  </View>
                </View>
              )}

              <View className='flex flex-row items-center gap-4 mb-5'>
                <FormLabel className='w-40'>Was the client aware?</FormLabel>
                <FormField
                  control={form.control}
                  name="client_aware"
                  render={({ field }) => (
                    <View className='flex-row items-center gap-2'>
                      <Label
                        nativeID='client_aware'
                        onPress={() => {
                          field.onChange((prev: boolean) => !prev);
                        }}
                      >
                        No
                      </Label>
                      <Switch checked={field.value} onCheckedChange={field.onChange} nativeID='client_aware' />
                      <Label
                        nativeID='client_aware'
                        onPress={() => {
                          field.onChange((prev: boolean) => !prev);
                        }}
                      >
                        Yes
                      </Label>
                    </View>
                  )}
                />
              </View>
              {form.watch('client_aware') && (
                <>
                  <View className='flex flex-row items-center gap-4 mb-5'>
                    <FormLabel className='w-40'>Did the crewman stay to make up time?</FormLabel>
                    <FormField
                      control={form.control}
                      name="make_up_time"
                      render={({ field }) => (
                        <View className='flex-row items-center gap-2'>
                          <Label
                            nativeID='make_up_time'
                            onPress={() => {
                              field.onChange((prev: boolean) => !prev);
                            }}
                          >
                            No
                          </Label>
                          <Switch checked={field.value} onCheckedChange={field.onChange} nativeID='make_up_time' />
                          <Label
                            nativeID='make_up_time'
                            onPress={() => {
                              field.onChange((prev: boolean) => !prev);
                            }}
                          >
                            Yes
                          </Label>
                        </View>
                      )}
                    />
                  </View>
                  {!form.watch('make_up_time') && (
                    <>
                      <View className='flex flex-row items-center gap-4 mb-5'>
                        <FormField
                          control={form.control}
                          name="make_up_time_comment"
                          render={({ field }) => (
                            <View className='flex-1'>
                              <FormLabel>Why not?</FormLabel>
                              <Textarea
                                {...field}
                                onChangeText={(value) => {
                                  field.onChange(value);
                                }}
                                value={field.value || ''}
                              />
                            </View>
                          )}
                        />
                      </View>
                      <View className='flex flex-row items-center gap-4 mb-5'>
                        <FormLabel className='w-40'>Was the job affected?</FormLabel>
                        <FormField
                          control={form.control}
                          name="job_affected"
                          render={({ field }) => (
                            <View className='flex-row items-center gap-2'>
                              <Label
                                nativeID='job_affected'
                                onPress={() => {
                                  field.onChange((prev: boolean) => !prev);
                                }}
                              >
                                No
                              </Label>
                              <Switch checked={field.value} onCheckedChange={field.onChange} nativeID='job_affected' />
                              <Label
                                nativeID='job_affected'
                                onPress={() => {
                                  field.onChange((prev: boolean) => !prev);
                                }}
                              >
                                Yes
                              </Label>
                            </View>
                          )}
                        />
                      </View>
                      {form.watch('job_affected') && (
                        <>
                          <View className='flex flex-row items-center gap-4 mb-5'>
                            <FormField
                              control={form.control}
                              name="job_affected_comment"
                              render={({ field }) => (
                                <View className='flex-1'>
                                  <FormLabel>Why?</FormLabel>
                                  <Textarea
                                    {...field}
                                    onChangeText={(value) => {
                                      field.onChange(value);
                                    }}
                                    value={field.value || ''}
                                  />
                                </View>
                              )}
                            />
                          </View>
                          <View className='flex flex-row items-center gap-4 mb-5'>
                            <FormLabel className='w-40'>Was the client upset?</FormLabel>
                            <FormField
                              control={form.control}
                              name="client_upset"
                              render={({ field }) => (
                                <View className='flex-row items-center gap-2'>
                                  <Label
                                    nativeID='client_upset'
                                    onPress={() => {
                                      field.onChange((prev: boolean) => !prev);
                                    }}
                                  >
                                    No
                                  </Label>
                                  <Switch checked={field.value} onCheckedChange={field.onChange} nativeID='client_upset' />
                                  <Label
                                    nativeID='client_upset'
                                    onPress={() => {
                                      field.onChange((prev: boolean) => !prev);
                                    }}
                                  >
                                    Yes
                                  </Label>
                                </View>
                              )}
                            />
                          </View>
                          {form.watch('client_upset') && (

                            <View className='flex flex-row items-center gap-4 mb-5'>
                              <FormField
                                control={form.control}
                                name="client_upset_comment"
                                render={({ field }) => (
                                  <View className='flex-1'>
                                    <FormLabel>Why?</FormLabel>
                                    <Textarea
                                      {...field}
                                      onChangeText={(value) => {
                                        field.onChange(value);
                                      }}
                                      value={field.value || ''}
                                    />
                                  </View>
                                )}
                              />
                            </View>

                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
          <View className='flex flex-row items-center gap-4 mb-5'>
            <FormLabel className='w-40'>Did they arrive 10 minutes early?</FormLabel>
            <FormField
              control={form.control}
              name="fifteen_mins_early"
              render={(props) => {
                const field = props.field as { value: boolean; onChange: (value: boolean) => void };
                return (
                  <View className='flex-row items-center gap-2'>
                    <Label
                      nativeID='fifteen_mins_early'
                      onPress={() => {
                        field.onChange(!field.value);
                      }}
                    >
                      No
                    </Label>
                    <Switch checked={field.value} onCheckedChange={field.onChange} nativeID='fifteen_mins_early' />
                    <Label
                      nativeID='fifteen_mins_early'
                      onPress={() => {
                        field.onChange(!field.value);
                      }}
                    >
                      Yes
                    </Label>
                  </View>
                );
              }}
            />
          </View>
          {!form.watch('fifteen_mins_early') && (
            <>
              <View className='mb-5'>
                <FormLabel className='mb-2'>When did they arrive?</FormLabel>
                <FormField
                  control={form.control}
                  name="arrival_status"
                  render={({ field }) => (
                    <Select
                      onValueChange={(option: Option | undefined) => field.onChange(option?.value || null)}
                      value={field.value ? { value: field.value, label: ARRIVAL_STATUS_LABELS[field.value] || field.value } : undefined}
                    >
                      <SelectTrigger className='w-full'>
                        <SelectValue
                          className='text-foreground text-sm native:text-lg'
                          placeholder='Select arrival status'
                        />
                      </SelectTrigger>
                      <SelectContent insets={contentInsets} className='w-full z-[1000] bg-white shadow-lg'>
                        <SelectGroup>
                          {Object.entries(ARRIVAL_STATUS_LABELS).map(([value, label]) => (
                            <SelectItem key={value} label={label} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  )}
                />
              </View>
              <View className='mb-5'>
                <FormField
                  control={form.control}
                  name="fifteen_mins_early_comment"
                  render={({ field }) => (
                    <View className='flex-1'>
                      <FormLabel>Why weren't they 10 minutes early?</FormLabel>
                      <Textarea
                        {...field}
                        onChangeText={(value) => {
                          field.onChange(value);
                        }}
                        value={field.value || ''}
                      />
                    </View>
                  )}
                />
              </View>
            </>
          )}
        </CardContent>
      </Card>
      {/* Overall rating and working-at-level are NOT strikes — they are an assessment, and
          leaving them under a card headed "Strikes" would imply a low rating costs somebody
          a strike. Separate card so the warning above only covers what it actually applies
          to. */}
      <Card className='mt-4 mb-4'>
        <CardHeader>
          <CardTitle>Overall</CardTitle>
        </CardHeader>
        <CardContent>
          <View className='flex flex-row items-center gap-4'>
            <FormLabel className='w-40'>Overall rating</FormLabel>
            <FormField
              control={form.control}
              name="overall_rating"
              render={({ field }) => (
                <View className="w-[160px]">
                  <StarRating
                    count={5}
                    size={20}
                    onFinishRating={(rating) => field.onChange(rating)}
                    selectedColor="#FFD700"
                    defaultRating={field.value as number || 0}
                  />
                  <View className="h-8">
                    <P className="text-center mt-2 text-xs">
                      {field.value === 1 && "Terrible - needs addressing"}
                      {field.value === 2 && "Below expectation"}
                      {field.value === 3 && "Meets expectation"}
                      {field.value === 4 && "Above expectation"}
                      {field.value === 5 && "Outstanding"}
                    </P>
                  </View>
                  {typeof field.value === 'number' && field.value > 0 && (
                    <View className="items-center mt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onPress={() => field.onChange(null)}
                        className="px-2 py-1"
                      >
                        Clear
                      </Button>
                    </View>
                  )}
                </View>
              )}
            />
          </View>
          {form.formState.errors.overall_rating && (
            <FormMessage><P className='text-red-500 text-sm'>Please select an overall rating</P></FormMessage>
          )}

          <View className='mb-5 mt-5'>
            <FormLabel className='mb-2'>What level is {crewMemberName || 'the crew member'} working at?</FormLabel>
            <FormField
              control={form.control}
              name="working_at_level"
              render={({ field }) => (
                <Select
                  onValueChange={(option: Option | undefined) => field.onChange(option?.value ? Number(option.value) : -1)}
                  value={field.value >= 0 && field.value <= 6 ? { value: String(field.value), label: field.value === 6 ? 'CC' : field.value === 0 ? 'Below Level 1' : `Level ${field.value}` } : undefined}
                >
                  <SelectTrigger className='w-full'>
                    <SelectValue
                      className='text-foreground text-sm native:text-lg'
                      placeholder='Select level'
                    />
                  </SelectTrigger>
                  <SelectContent insets={contentInsets} className='w-full z-[1000] bg-white shadow-lg'>
                    <SelectGroup>
                      <SelectItem label="CC" value="6">
                        CC
                      </SelectItem>
                      <SelectItem label="Level 5" value="5">
                        Level 5
                      </SelectItem>
                      <SelectItem label="Level 4" value="4">
                        Level 4
                      </SelectItem>
                      <SelectItem label="Level 3" value="3">
                        Level 3
                      </SelectItem>
                      <SelectItem label="Level 2" value="2">
                        Level 2
                      </SelectItem>
                      <SelectItem label="Level 1" value="1">
                        Level 1
                      </SelectItem>
                      <SelectItem label="Below Level 1" value="0">
                        Below Level 1
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              )}
            />
          </View>
          {form.formState.errors.working_at_level && (
            <FormMessage><P className='text-red-500 text-sm'>Please select a working level</P></FormMessage>
          )}

          {form.watch('overall_rating') && [1, 2, 5].includes(form.watch('overall_rating') as number) ? (
            <View className='flex flex-row items-center gap-4 mb-5'>
              <FormField
                control={form.control}
                name="overall_rating_comments"
                render={({ field }) => (
                  <View className='flex-1'>
                    <FormLabel>Please add a comment</FormLabel>
                    <Textarea
                      {...field}
                      onChangeText={(value) => {
                        field.onChange(value);
                      }}
                      value={field.value || ''}
                    />
                    <FormMessage />
                  </View>
                )}
              />
            </View>
          ) : <></>}
        </CardContent>
      </Card>
      <Card className='mt-4 mb-8'>
        <CardHeader>
          <CardTitle>Skills feedback</CardTitle>
          <CardDescription>rate where applicable</CardDescription>
        </CardHeader>
        <CardContent>
          {Object.entries(ratingFields).map(([key, label]) => (
            <View key={key} className='flex flex-row items-center gap-4 mb-5'>
              <FormLabel className='w-40'>{label}</FormLabel>
              <FormField
                control={form.control}
                name={key as keyof JobCrewFeedbackFormValues}
                render={({ field }) => (
                  <View className="w-[160px]">
                    <StarRating
                      count={5}
                      size={20}
                      onFinishRating={(rating) => field.onChange(rating)}
                      selectedColor="#FFD700"
                      defaultRating={field.value as number || 0}
                    />
                    <View className="h-8">
                      <P className="text-center mt-2 text-xs">
                        {!field.value && "Not applicable"}
                        {field.value === 1 && "Terrible - needs addressing"}
                        {field.value === 2 && "Below expectation"}
                        {field.value === 3 && "Meets expectation"}
                        {field.value === 4 && "Above expectation"}
                        {field.value === 5 && "Outstanding"}
                      </P>
                    </View>
                    {typeof field.value === 'number' && field.value > 0 && (
                      <View className="items-center mt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onPress={() => field.onChange(null)}
                          className="px-2 py-1"
                        >
                          Clear
                        </Button>
                      </View>
                    )}
                  </View>
                )}
              />
            </View>
          ))}
          <View className='flex flex-row items-center gap-4 mb-5'>
            <FormField
              control={form.control}
              name="rating_comments"
              render={({ field }) => (
                <View className='flex-1'>
                  <FormLabel>Comments</FormLabel>
                  <Textarea
                    {...field}
                    onChangeText={(value) => {
                      field.onChange(value);
                    }}
                    value={field.value || ''}
                  />
                  <FormMessage />
                </View>
              )}
            />
          </View>

          <View className='flex flex-row items-center gap-4 mb-5 mt-3'>
            <FormLabel className='w-40 text-sm'>Show in crew average?</FormLabel>
            <FormField
              control={form.control}
              name="hide_average"
              render={({ field }) => (
                <View className='flex-row items-center gap-2'>
                  <Label
                    nativeID='hide_average'
                    onPress={() => {
                      field.onChange((prev: boolean) => !prev);
                    }}
                  >
                    Show
                  </Label>
                  <Switch checked={field.value} onCheckedChange={field.onChange} nativeID='hide_average' />
                  <Label
                    nativeID='hide_average'
                    onPress={() => {
                      field.onChange((prev: boolean) => !prev);
                    }}
                  >
                    Hide
                  </Label>
                </View>
              )}
            />
          </View>

          <Button className='mt-4 mb-4' size="default"
            variant="crew"
            onPress={form.handleSubmit(onSubmit)}
            disabled={isPending}
          >
            {isPending ? 'Submitting...' : 'Submit feedback'}
          </Button>
        </CardContent>
      </Card>
    </Form>
  )
}
